import LabelWithTooltip from "@/components/form/labels/LabelWithTooltip";
import { datePickerFormItemProps, datePickerStandardProps, selectStandardProps } from "@/utils/componentUtils";
import { fillFormBirthDateFromPin, upperCaseStringNormalizeFunction } from "@/utils/formUtils";
import { regexPatterns, validations } from "@/utils/validationUtils";
import { App as AntApp, Col, DatePicker, Form, Input, Row, Select } from "antd";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import { useSearchAgent } from "../../../../../common/api/queries";
import { rowGutter } from "../../../../../common/constants";
import { EducationLevel } from "../../../../../common/enums";
import AcademicDegreeAfterSelect from "../../../../enumerations/components/form/AcademicDegreeAfterSelect";
import AcademicDegreeSelect from "../../../../enumerations/components/form/AcademicDegreeSelect";
import { SecondaryAgentType } from "../../../enums";
import {
  CreateUpdateNaturalSecondaryAgent,
  CreateUpdateSecondaryAgent,
  CreateUpdateSelfEmployedSecondaryAgent,
} from "../../../types";
import {
  convertPartialAgentToPartialCreateUpdateSecondaryAgent,
  convertSecondaryAgentTypeToAgentType,
} from "../../../utils";

const colSpan = 4;

type Props = {
  allowSearch?: boolean;
};

export const NaturalSecondaryAgentDataFormPart = ({ allowSearch }: Props) => {
  const { t } = useTranslation();
  const { message } = AntApp.useApp();

  const agentSearch = useSearchAgent();

  const form = Form.useFormInstance<CreateUpdateNaturalSecondaryAgent | CreateUpdateSelfEmployedSecondaryAgent>();
  const type = Form.useWatch("type", form);

  const handlePinSearchOrChange = () => {
    form.validateFields(["personalIdentificationNumber"]).then(async (values) => {
      fillFormBirthDateFromPin(values.personalIdentificationNumber, form);
      const searchResult = await agentSearch.mutateAsync({
        keyword: values.personalIdentificationNumber,
        agentType: convertSecondaryAgentTypeToAgentType(type),
      });

      if (searchResult.data) {
        message.success(t("agent.helpers.agentFound"));

        const formData = convertPartialAgentToPartialCreateUpdateSecondaryAgent(
          searchResult.data,
        ) as Partial<CreateUpdateSecondaryAgent> & { type: SecondaryAgentType.NATURAL };

        form.setFieldsValue(formData);
      }
    });
  };

  return (
    <>
      {type === SecondaryAgentType.NATURAL && (
        <Row gutter={rowGutter}>
          <Col span={colSpan}>
            <Form.Item
              name="personalIdentificationNumber"
              label={
                <LabelWithTooltip
                  tooltip={t("helpers.pinFormat")}
                  label={t("agent.attrs.personalIdentificationNumberLong")}
                />
              }
              rules={[validations.notBlank, validations.pin]}
            >
              {allowSearch ? (
                <Input.Search
                  loading={agentSearch.isPending}
                  onSearch={handlePinSearchOrChange}
                  onChange={handlePinSearchOrChange}
                />
              ) : (
                <Input onChange={(e) => fillFormBirthDateFromPin(e.target.value, form)} />
              )}
            </Form.Item>
          </Col>
        </Row>
      )}

      <Row gutter={rowGutter}>
        <Col span={colSpan}>
          <AcademicDegreeSelect
            formItemProps={{
              name: "academicDegree",
              label: t("agent.attrs.academicDegree"),
            }}
            selectProps={{ allowClear: true }}
          />
        </Col>

        <Col span={colSpan}>
          <Form.Item
            name="firstName"
            label={t("agent.attrs.firstName")}
            rules={[validations.notBlank, validations.size(1, 255), validations.pattern(regexPatterns.wordRegex)]}
          >
            <Input />
          </Form.Item>
        </Col>

        <Col span={colSpan}>
          <Form.Item
            name="lastName"
            label={t("agent.attrs.lastName")}
            rules={[
              validations.notBlank,
              validations.size(1, 255),
              validations.pattern(regexPatterns.wordRegex),
              validations.pattern(regexPatterns.agentSortNameRegex),
            ]}
          >
            <Input />
          </Form.Item>
        </Col>

        <Col span={colSpan}>
          <AcademicDegreeAfterSelect
            formItemProps={{ name: "academicDegreeAfter", label: t("agent.attrs.academicDegreeAfter") }}
            selectProps={{ allowClear: true }}
          />
        </Col>

        <Col span={8}>
          <Form.Item name="educationLevel" rules={[validations.none]} label={t("common.enums.educationLevel._label")}>
            <Select
              {...selectStandardProps}
              allowClear
              options={Object.values(EducationLevel).map((level) => ({
                label: t(`common.enums.educationLevel.${level}`),
                value: level,
              }))}
            />
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={rowGutter}>
        {type === SecondaryAgentType.SELF_EMPLOYED && (
          <Col span={colSpan}>
            <Form.Item
              name="personalIdentificationNumber"
              label={
                <LabelWithTooltip
                  tooltip={t("helpers.pinFormat")}
                  label={t("agent.attrs.personalIdentificationNumberLong")}
                />
              }
              rules={[validations.notBlank, validations.pin]}
            >
              <Input onChange={(event) => fillFormBirthDateFromPin(event.target.value, form)} />
            </Form.Item>
          </Col>
        )}

        <Col span={colSpan}>
          <Form.Item
            name="birthDate"
            label={t("agent.attrs.birthDate")}
            rules={[validations.notNull, validations.notPresentAndFuture]}
            {...datePickerFormItemProps}
          >
            <DatePicker {...datePickerStandardProps} showNow={false} maxDate={dayjs().subtract(1, "day")} />
          </Form.Item>
        </Col>

        <Col span={colSpan}>
          <Form.Item
            name="identityCardNumber"
            label={t("agent.attrs.identityCardNumber")}
            rules={[validations.size(8, 10), validations.pattern(regexPatterns.idCardRegex)]}
            normalize={upperCaseStringNormalizeFunction}
          >
            <Input />
          </Form.Item>
        </Col>
      </Row>
    </>
  );
};
